import {useEffect, useState} from 'react'
import clsx from 'clsx'
import {backupCodesFilename, buildBackupCodesText} from '../mfa/mfaCodes'
import {copyToClipboard, downloadTextFile} from '../mfa/clipboard'

interface Props {
  codes: string[]
  /** Shown in the downloaded file header only. */
  email?: string | null
  /** The parent commits the session token and redirects when this fires. */
  onContinue: () => void
}

/**
 * Shown exactly once, right after enrollment. The session token is NOT persisted
 * until the user ticks "I have saved these codes" and clicks Continue.
 */
export function BackupCodesStep({codes, email, onContinue}: Props) {
  const [saved, setSaved] = useState(false)
  const [copied, setCopied] = useState(false)
  const [downloaded, setDownloaded] = useState(false)

  useEffect(() => {
    if (!copied) return
    const t = window.setTimeout(() => setCopied(false), 2500)
    return () => window.clearTimeout(t)
  }, [copied])

  const handleCopyAll = async () => {
    const ok = await copyToClipboard(codes.join('\n'))
    setCopied(ok)
  }

  const handleDownload = () => {
    const now = new Date()
    downloadTextFile(backupCodesFilename(now), buildBackupCodesText(codes, {email, issuedAt: now}))
    setDownloaded(true)
  }

  return (
    <div className='form w-100' id='kt_login_mfa_backup_codes'>
      {/* begin::Heading */}
      <div className='text-center mb-8'>
        <h1 className='text-dark fw-bolder mb-3'>Save your backup codes</h1>
        <div className='text-gray-500 fw-semibold fs-6'>
          Two-step verification is now active. If you lose access to your authenticator app, one of
          these codes will let you sign in. Each code works <strong>once</strong>. They are shown{' '}
          <strong>only now</strong> — store them somewhere safe.
        </div>
      </div>
      {/* end::Heading */}

      {/* begin::Codes */}
      <div className='bg-light rounded p-6 mb-6'>
        <div className='row g-3'>
          {codes.map((code, i) => (
            <div className='col-6' key={code}>
              <code className='d-block text-center fs-4 fw-bold text-dark bg-white rounded py-2 px-3 border border-gray-300'>
                <span className='text-gray-500 fs-8 me-2'>{String(i + 1).padStart(2, '0')}</span>
                {code}
              </code>
            </div>
          ))}
        </div>
      </div>
      {/* end::Codes */}

      {/* begin::Actions */}
      <div className='d-flex gap-3 mb-8'>
        <button
          type='button'
          className={clsx('btn flex-grow-1', copied ? 'btn-light-success' : 'btn-light-primary')}
          onClick={handleCopyAll}
        >
          <i className='bi bi-clipboard me-2'></i>
          {copied ? 'Copied to clipboard' : 'Copy all'}
        </button>
        <button
          type='button'
          className={clsx('btn flex-grow-1', downloaded ? 'btn-light-success' : 'btn-light-primary')}
          onClick={handleDownload}
        >
          <i className='bi bi-download me-2'></i>
          {downloaded ? 'Downloaded' : 'Download .txt'}
        </button>
      </div>
      {/* end::Actions */}

      {/* begin::Confirm */}
      <div className='fv-row mb-8'>
        <label className='form-check form-check-custom form-check-solid align-items-start' htmlFor='kt_login_mfa_codes_saved'>
          <input
            className='form-check-input mt-1'
            type='checkbox'
            id='kt_login_mfa_codes_saved'
            checked={saved}
            onChange={(e) => setSaved(e.target.checked)}
          />
          <span className='form-check-label fw-semibold text-gray-700 fs-6 ms-3'>
            I have saved these codes somewhere safe. I understand they will not be shown again.
          </span>
        </label>
      </div>
      {/* end::Confirm */}

      {/* begin::Action */}
      <div className='d-grid'>
        <button
          type='button'
          id='kt_login_mfa_codes_continue'
          className='btn btn-primary'
          disabled={!saved}
          onClick={() => {
            if (saved) onContinue()
          }}
        >
          <span className='indicator-label'>Continue to panel</span>
        </button>
      </div>
      {/* end::Action */}
    </div>
  )
}
